import type * as React from 'react';

import { Link } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import { color } from '../constants/color.css';

type Props = {
  href?: string;
  target?: React.ComponentProps<typeof html.a>['target'];
  rel?: string;
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.ComponentProps<typeof html.a>['style'];
};

/**
 * A text link in the accent colour. Internal destinations navigate through
 * react-router (client-side) while external links stay real anchors; both get
 * their styling from this RSD component rather than a global `a` reset.
 */
export default function AccentTextLink({
  href,
  target,
  rel,
  onClick,
  children,
  style,
}: Props) {
  if (isInternalHref(href) && target !== '_blank') {
    return (
      <Link to={href} onClick={onClick} {...css.props(styles.link, style)}>
        {children}
      </Link>
    );
  }

  return (
    <html.a
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      style={[styles.link, style]}
    >
      {children}
    </html.a>
  );
}

function isInternalHref(href: string | undefined): href is string {
  return !!href && href.startsWith('/') && !href.startsWith('//');
}

const styles = css.create({
  link: {
    color: color.link,
    cursor: 'pointer',
    textDecorationLine: {
      default: 'none',
      ':hover': 'underline',
    },
  },
});
